import { PAGE_DEFAULT_VALUE } from '../../../../../constants/route'
import { getItemsIds } from '../../../../../utils/common'
import { REQUEST_BOARD_COLUMN_PAGE_SIZE } from '../../RequestBoard.constants'
import { getRequestBoardItemFilters } from '../../RequestBoard.utils'

function isRequestBoardItemShown({ column, id }) {
  return getItemsIds(column.items).includes(id)
}

function isRequestBoardColumnUnchanged({ column, generations }) {
  return column.reloadGenerationRef.current === generations[column.status]
}

function isRequestBoardItemLeavingColumn({ column, id, item }) {
  return (
    isRequestBoardItemShown({ column, id }) && item?.status !== column.status
  )
}

function isRequestBoardColumnPageLoadOverlapping({ column, pageGenerations }) {
  return (
    column.isLoadingMore ||
    column.pageGenerationRef.current !== pageGenerations[column.status]
  )
}

function isRequestBoardItemMoving({ columns, id }) {
  return columns.some(column => column.departedIds.includes(id))
}

function getRequestBoardColumnStatuses(columns) {
  return columns.map(column => column.status)
}

function isRequestBoardPlacementColumn({ column, placement }) {
  return !column.isLoading && !placement.loadingStatuses.includes(column.status)
}

function isRequestBoardItemShownAsBefore({ column, id, placement }) {
  return (
    isRequestBoardItemShown({ column, id }) ===
    placement.shownStatuses.includes(column.status)
  )
}

export function getRequestBoardColumnGenerations(columns) {
  return Object.fromEntries(
    columns.map(column => [column.status, column.reloadGenerationRef.current])
  )
}

export function getRequestBoardColumnPageGenerations(columns) {
  return Object.fromEntries(
    columns.map(column => [column.status, column.pageGenerationRef.current])
  )
}

export function getRequestBoardUnchangedColumns({ columns, generations }) {
  return columns.filter(column =>
    isRequestBoardColumnUnchanged({ column, generations })
  )
}

export function getRequestBoardItemParams({ filterValues, id }) {
  return {
    filters: getRequestBoardItemFilters({ filterValues, id }),
    page: PAGE_DEFAULT_VALUE,
    size: REQUEST_BOARD_COLUMN_PAGE_SIZE,
  }
}

export function getRequestBoardItem({ id, items }) {
  return items.find(item => item.id === id) ?? null
}

export function getRequestBoardItemPlacement({ columns, id }) {
  return {
    isMoving: isRequestBoardItemMoving({ columns, id }),
    loadingStatuses: getRequestBoardColumnStatuses(
      columns.filter(column => column.isLoading)
    ),
    shownStatuses: getRequestBoardColumnStatuses(
      columns.filter(column => isRequestBoardItemShown({ column, id }))
    ),
  }
}

// A drag moves the card between columns that keep their lists. A column that
// reloaded, or was loading, hides or shows the card on its own, so it is no
// sign of a move.
export function isRequestBoardItemPlacementKept({
  columns,
  id,
  placement,
  unchangedColumns,
}) {
  return (
    !placement.isMoving &&
    !isRequestBoardItemMoving({ columns, id }) &&
    unchangedColumns
      .filter(column => isRequestBoardPlacementColumn({ column, placement }))
      .every(column =>
        isRequestBoardItemShownAsBefore({ column, id, placement })
      )
  )
}

export function isRequestBoardColumnReloadNeeded({
  column,
  id,
  item,
  pageGenerations,
}) {
  return (
    column.isLoading ||
    column.hasError ||
    (isRequestBoardItemLeavingColumn({ column, id, item }) &&
      isRequestBoardColumnPageLoadOverlapping({ column, pageGenerations }))
  )
}
